import { useEffect } from "react";
import { useReservation } from "../../hooks/useReservation";
import { MdCreditCard, MdWarning } from "react-icons/md";
import { useNavigate } from "react-router-dom";

const ReservationTable = () => {
  const { getActiveReservationsWithLinkedVms, reservations, loading, error } = useReservation();
  const navigate = useNavigate();

  useEffect(() => {
    getActiveReservationsWithLinkedVms();
  }, []);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const isExpiringSoon = (expiryDate) => {
    const expiry = new Date(expiryDate);
    const now = new Date();
    const daysLeft = Math.ceil((expiry - now) / (1000 * 60 * 60 * 24));
    return daysLeft <= 30 && daysLeft > 0;
  };

  if (loading) {
    return (
      <div className="bg-primary-dark rounded-2xl p-4 sm:p-6 m-2 sm:m-4 md:m-8">
        <div className="text-center py-8">
          <div className="inline-block w-8 h-8 border-4 border-primary-cyan border-t-transparent rounded-full animate-spin"></div>
          <p className="text-gray-400 mt-3">Loading reservations...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-primary-dark rounded-2xl p-4 sm:p-6 m-2 sm:m-4 md:m-8">
        <div className="text-center py-8">
          <p className="text-red-400">Error loading reservations: {error}</p>
        </div>
      </div>
    );
  }

  if (!reservations || reservations.length === 0) {
    return (
      <div className="bg-primary-dark rounded-2xl p-4 sm:p-6 m-2 sm:m-4 md:m-8">
        <div className="flex items-center gap-2 mb-6 pb-3 border-b border-gray-700">
          <MdCreditCard className="text-primary-cyan text-2xl" />
          <h3 className="text-xl font-semibold text-white">Azure Reservations</h3>
        </div>
        <div className="text-center py-8">
          <p className="text-gray-400">No active reservations found</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-primary-dark rounded-2xl p-4 sm:p-6 m-2 sm:m-4 md:m-8">
      <div className="flex items-center gap-2 mb-6 pb-3 border-b border-gray-700">
        <MdCreditCard className="text-primary-cyan text-2xl" />
        <h3 className="text-xl font-semibold text-white">Azure Reservations</h3>
      </div>

      <div className="overflow-x-auto -mx-4 sm:mx-0 px-4 sm:px-0">
        <div className="min-w-[800px] sm:min-w-0">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-700">
                <th className="text-left py-3 px-2 sm:px-3 text-gray-400 font-medium text-xs sm:text-sm">Display Name</th>
                <th className="text-left py-3 px-2 sm:px-3 text-gray-400 font-medium text-xs sm:text-sm">VM Type</th>
                <th className="text-left py-3 px-2 sm:px-3 text-gray-400 font-medium text-xs sm:text-sm">Purchase Date</th>
                <th className="text-left py-3 px-2 sm:px-3 text-gray-400 font-medium text-xs sm:text-sm">Expiry Date</th>
                <th className="text-left py-3 px-2 sm:px-3 text-gray-400 font-medium text-xs sm:text-sm">Covered VMs</th>
                <th className="text-left py-3 px-2 sm:px-3 text-gray-400 font-medium text-xs sm:text-sm">Status</th>
               </tr>
            </thead>
            <tbody>
              {reservations.map((reservation, idx) => (
                <tr
                  key={idx}
                  className="border-b border-gray-700/50 hover:bg-white/5 transition"
                >
                  <td className="py-3 px-2 sm:px-3">
                    <span className="text-white font-medium text-xs sm:text-sm break-words">
                      {reservation.displayName}
                    </span>
                   </td>
                  <td className="py-3 px-2 sm:px-3">
                    <span className="text-gray-300 font-mono text-[10px] sm:text-xs">
                      {reservation.vmType}
                    </span>
                   </td>
                  <td className="py-3 px-2 sm:px-3">
                    <span className="text-gray-300 text-xs sm:text-sm">
                      {formatDate(reservation.purchaseDateTime)}
                    </span>
                   </td>
                  <td className="py-3 px-2 sm:px-3">
                    <span
                      className={`${isExpiringSoon(reservation.expiryDateTime) ? "text-yellow-400" : "text-gray-300"} text-xs sm:text-sm`}
                    >
                      {formatDate(reservation.expiryDateTime)}
                    </span>
                   </td>
                  <td className="py-3 px-2 sm:px-3">
                    <div className="flex flex-wrap gap-1">
                      {reservation.linkedVms.map((vm, vmIdx) => (
                        <span
                          key={vmIdx}
                          className="bg-gray-700/50 text-gray-300 px-2 py-0.5 rounded text-[10px] sm:text-xs"
                        >
                          {vm.vmName}
                        </span>
                      ))}
                    </div>
                   </td>
                  <td className="py-3 px-2 sm:px-3">
                    {new Date(reservation.expiryDateTime) > new Date() ? (
                      <span className="bg-green-500/20 text-green-400 px-2 py-1 rounded text-[10px] sm:text-xs font-medium">
                        Active
                      </span>
                    ) : (
                      <span className="bg-red-500/20 text-red-400 px-2 py-1 rounded text-[10px] sm:text-xs font-medium">
                        Expired
                      </span>
                    )}
                    {isExpiringSoon(reservation.expiryDateTime) && (
                      <div className="flex items-center gap-1 mt-1 text-yellow-400 text-[10px] sm:text-xs">
                        <MdWarning className="w-3 h-3" />
                        Expiring soon
                      </div>
                    )}
                   </td>
                 </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="mt-6 pt-4 border-t border-gray-700">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="text-gray-400 text-xs sm:text-sm text-center sm:text-left">
            Total Reservations: {reservations.length} | Total VMs Covered:{" "}
            {reservations.reduce((sum, r) => sum + r.linkedVms.length, 0)}
          </div>
          <div>
            <button onClick={() => navigate("/settings")} className="bg-primary-cyan border border-primary-cyan hover:bg-primary-cyan/80 text-white px-3 sm:px-4 py-2 rounded-lg font-medium transition text-xs sm:text-sm">
              Change Billing Type
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReservationTable;